import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PopoverPosition } from "@blueprintjs/core";
import { Tooltip } from "@blueprintjs/core";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import TextField from "components/Controls/TextField";
import TextTruncate from "components/TextTruncate";
import { checkPressedEnter } from "utils/keyboard";
import useOutsideClick from "hooks/useClickOutside";
import { setRenamingProgress } from "store/global/global.actions";
import { renamingProgressSelector } from "store/global/global.selectors";
import { MAX_NAME_LENGTH } from "store/ps-workspace/ps-workspace.utils";
import { getEntityTypeTitle } from "store/global/global.utils";
import * as styles from "./styles.module.scss";
export var RenamableStyles;
(function (RenamableStyles) {
    RenamableStyles["default"] = "default";
    RenamableStyles["inverted"] = "inverted";
    RenamableStyles["list"] = "list";
    RenamableStyles["highlighted"] = "highlighted";
    RenamableStyles["fullWidth"] = "fullWidth";
})(RenamableStyles || (RenamableStyles = {}));
const RenamableCore = ({ text, toolbarItems, classes = {}, clearable = false, maxLabelLength = MAX_NAME_LENGTH, http = false, editableByDefault = false, alwaysEditable = false, style = RenamableStyles.default, fullWidth, inline = true, getExternalError, onRename, clickOutside, onTextClick, inputProps, excludeOutsideRefs = [], entityType, placeholder, readonly = false, autoFocus, }) => {
    const dispatch = useDispatch();
    const renamableItemRef = useRef(null);
    const textContainerRef = useRef(null);
    const isRenameInProgress = useSelector(renamingProgressSelector);
    const [renameModeIsOn, setRenameModeIsOn] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [hasError, setHasError] = useState(false);
    const [value, setValue] = useState(text);
    const { isOutside } = useOutsideClick(renamableItemRef, excludeOutsideRefs);
    const entityTypeTitle = useMemo(() => getEntityTypeTitle(entityType), [entityType]);
    const externalErrorMessage = useMemo(() => getExternalError(http), [getExternalError, http]);
    const localReset = useCallback(() => {
        setHasError(false);
        setErrorMessage("");
        !alwaysEditable && setRenameModeIsOn(false);
    }, [alwaysEditable]);
    const submitRename = useCallback((e) => {
        let { value } = e.target;
        value = value.trim();
        const onClickOutside = (hasError) => e.type === "blur" && clickOutside && clickOutside(hasError, true, value);
        if (!value && !clearable && !placeholder) {
            setHasError(true);
            setErrorMessage(entityTypeTitle ? `${entityTypeTitle} name cannot be empty.` : "Name cannot be empty.");
            onClickOutside(true);
            return;
        }
        if (onRename && (value || clearable)) {
            setValue(value);
            if (value !== text) {
                onRename(value);
            }
            onClickOutside();
        }
        localReset();
        dispatch(setRenamingProgress(false));
    }, [clearable, placeholder, clickOutside, dispatch, localReset, onRename, text, entityTypeTitle]);
    const submitByBlur = useCallback((e) => submitRename(e), [submitRename]);
    const submitByKeyPress = useCallback((e) => {
        if (e.key === "Escape") {
            localReset();
            e.target.value = text;
            dispatch(setRenamingProgress(false));
        }
        if (checkPressedEnter(e)) {
            submitRename(e);
        }
    }, [dispatch, localReset, submitRename, text]);
    const onTextContainerClick = useCallback((e) => {
        e.stopPropagation();
        if (!isRenameInProgress) {
            setRenameModeIsOn(true);
            dispatch(setRenamingProgress(true));
        }
    }, [dispatch, isRenameInProgress]);
    const onChange = useCallback(() => {
        if (hasError) {
            setHasError(false);
            setErrorMessage("");
        }
    }, [hasError]);
    const onFocus = useCallback((e) => {
        const { value } = e.target;
        e.target.value = "";
        e.target.value = value;
        editableByDefault && !alwaysEditable
            ? e.target.select()
            : e.target.setSelectionRange(value.length, value.length);
    }, [alwaysEditable, editableByDefault]);
    useEffect(() => {
        setValue(text);
    }, [text]);
    useEffect(() => {
        if (externalErrorMessage) {
            setRenameModeIsOn(true);
            setHasError(true);
            setErrorMessage(externalErrorMessage);
            dispatch(setRenamingProgress(true));
        }
        return () => {
            if (isRenameInProgress && renameModeIsOn) {
                dispatch(setRenamingProgress(false));
            }
        };
    }, [
        dispatch,
        editableByDefault,
        externalErrorMessage,
        getExternalError,
        http,
        isRenameInProgress,
        renameModeIsOn,
    ]);
    useEffect(() => {
        !renameModeIsOn && isOutside && clickOutside && clickOutside(false, isOutside);
    }, [clickOutside, isOutside, renameModeIsOn]);
    useEffect(() => {
        if (editableByDefault || alwaysEditable) {
            setRenameModeIsOn(true);
            dispatch(setRenamingProgress(true));
        }
        else {
            localReset();
        }
    }, [alwaysEditable, dispatch, editableByDefault, localReset]);
    useEffect(() => {
        if (inline) {
            dispatch(setRenamingProgress(false));
            setHasError(false);
            setErrorMessage("");
            !editableByDefault && !alwaysEditable && setRenameModeIsOn(false);
            setValue(text);
        }
    }, [editableByDefault, localReset, inline, dispatch, text, alwaysEditable]);
    useEffect(() => {
        if (!autoFocus) {
            return;
        }
        if (!!textContainerRef?.current && !renameModeIsOn) {
            setTimeout(() => textContainerRef?.current?.focus(), 300);
        }
    }, [autoFocus, renameModeIsOn]);
    const styleClassNames = useMemo(() => {
        if (!style) {
            return [];
        }
        else {
            return typeof style === "string" ? [styles[style]] : style.map((s) => styles[s]);
        }
    }, [style]);
    return (React.createElement("div", { ref: renamableItemRef, className: classNames(styles["renamable-root"], ...styleClassNames, classes.root, {
            [styles.renameMode]: renameModeIsOn,
            [styles.fullWidth]: fullWidth,
        }) },
        React.createElement("div", { className: classNames(styles.container, classes.container, {
                [styles.textAreaContainer]: !inline,
                [styles.error]: hasError,
            }) }, renameModeIsOn ? (React.createElement(React.Fragment, null,
            React.createElement(Tooltip, { portalClassName: classNames(styles["tooltip-portal"], classes.tooltipPortal, {
                    [styles["tooltip-portal-alternative"]]: style === RenamableStyles.inverted,
                }), position: PopoverPosition.BOTTOM, usePortal: true, content: errorMessage, isOpen: !!errorMessage, disabled: !errorMessage },
                React.createElement(TextField, { autoFocus: true, maxLength: maxLabelLength, textarea: !inline, outlined: false, defaultValue: hasError ? value : text, classes: { input: styles.input }, onBlur: submitByBlur, onKeyDown: submitByKeyPress, onClick: (e) => e.stopPropagation(), onChange: onChange, onFocus: onFocus, ...inputProps })))) : (React.createElement("div", { tabIndex: 0, ref: textContainerRef, onClick: onRename && !readonly ? onTextContainerClick : undefined, onKeyDown: (e) => {
                if (checkPressedEnter(e) && onRename && !readonly) {
                    onTextContainerClick(e);
                }
            }, className: classNames(styles.textContainer, {
                [styles.disabled]: isRenameInProgress || !onRename || readonly,
                [styles.fixedPencil]: !inline,
            }) },
            React.createElement(TextTruncate, { text: value, wrapText: !inline, className: classNames(styles.text, { [styles.preWrap]: !inline }), ...(onTextClick ? { onClick: onTextClick } : {}) })))),
        React.createElement("div", { className: classNames(styles.toolbarWrapper, classes.toolbar) }, toolbarItems)));
};
export default RenamableCore;
